export const generateConfigurationTemplate = (
  t: (key: string) => string,
  brands: { name: string; slug: string }[]
) => ({
  // Feature flags that the widget actively uses in best-match
  activeFeatures: {
    key: "features",
    title: t("configurationTemplate.featuresTitle"),
    description: "Controls which features are enabled in the widget",
    items: [
      {
        key: "camera_analysis",
        title: t("configurationTemplate.featuresCameraAnalysis"),
        description: "Enable face analysis for seasonal color analysis",
      },
      {
        key: "kits",
        title: "Kits",
        description: "Enable kit selection for makeup products",
      },
      {
        key: "redirect_to_product_page",
        title: t("configurationTemplate.featuresRedirectToProductPage"),
        description: "Redirect to product page instead of cart actions",
      },
      {
        key: "enable_go_to_cart",
        title: t("configurationTemplate.featuresEnableGoToCart"),
        description: "Show Go to Cart button when cart is empty",
      },
      {
        key: "compare_hidden",
        title: "Hide Compare",
        description: "Hide split-screen compare in viewer",
      },
      {
        key: "save_option_enabled",
        title: "Save Option",
        description: "Show save button for captures",
      },
      {
        key: "camera_devices_select_enabled",
        title: "Camera Selector",
        description: "Allow switching between front/rear cameras",
      },
      {
        key: "close_button_inside_widget",
        title: "Close Button Inside",
        description: "Place close button inside the widget",
      },
      {
        key: "go_to_cart_hidden",
        title: "Hide Go to Cart",
        description: "Hide Go to Cart button in cart actions",
      },
      {
        key: "add_to_cart_hidden",
        title: "Hide Add to Cart",
        description: "Hide Add to Cart button in cart actions",
      },
    ],
  },

  // Additional features (not used by best-match directly, but stored in config)
  additionalFeatures: {
    key: "features",
    title: "Additional Features",
    description: "Features stored in config but not actively used by best-match",
    items: [
      {
        key: "makeup_virtual_try_on",
        title: t("configurationTemplate.featuresMakeupVirtualTryOn"),
      },
      {
        key: "beauty_ai_advisor",
        title: t("configurationTemplate.featuresBeautyAiAdvisor"),
      },
      {
        key: "analytics",
        title: t("configurationTemplate.featuresAnalytics"),
      },
      {
        key: "makeup_transfer",
        title: t("configurationTemplate.featuresMakeupTransfer"),
      },
      {
        key: "gift_selection",
        title: t("configurationTemplate.featuresGiftSelection"),
      },
      {
        key: "skin_care",
        title: t("configurationTemplate.featuresSkinCare"),
      },
      {
        key: "products_links",
        title: t("configurationTemplate.featuresProductsLinks"),
      },
      {
        key: "hide_sidebar",
        title: t("configurationTemplate.featuresHideSidebar"),
      },
      {
        key: "hide_hide_show",
        title: t("configurationTemplate.featuresHideHideShow"),
      },
      {
        key: "hide_cart_arrow",
        title: t("configurationTemplate.featuresHideCartArrow"),
      },
      {
        key: "use_legacy_labelling",
        title: t("configurationTemplate.featuresUseLegacyLabelling"),
      },
      {
        key: "custom_translation",
        title: t("configurationTemplate.featuresCustomTranslation"),
      },
      {
        key: "enable_iamge_upload_during_analysis",
        title: t("configurationTemplate.featuresEnableImageUploadDuringAnalysis"),
      },
    ],
  },

  // Product categories
  categories: {
    key: "categories",
    title: t("configurationTemplate.categoriesTitle"),
    description: "Product categories available in the widget",
    items: [
      { key: "makeup", title: t("configurationTemplate.categoriesMakeup") },
      { key: "hats", title: t("configurationTemplate.categoriesHats") },
      { key: "glasses", title: t("configurationTemplate.categoriesGlasses") },
      { key: "scarves", title: t("configurationTemplate.categoriesScarves") },
      { key: "jewelry", title: t("configurationTemplate.categoriesJewelry") },
      { key: "eye_lenses", title: t("configurationTemplate.categoriesEyeLenses") },
      { key: "eyelashes", title: t("configurationTemplate.categoriesEyelashes") },
      { key: "hairs", title: t("configurationTemplate.categoriesHairs") },
    ],
  },

  // Select fields
  selects: [
    {
      key: "brand",
      title: t("configurationTemplate.brandTitle"),
      options: [
        { key: "multibrand", title: t("configurationPage.multibrand") },
        ...brands.map(({ name, slug }) => ({ key: slug, title: name })),
      ],
    },
    {
      key: "entry",
      title: t("configurationTemplate.entryPointTitle"),
      options: [
        { key: "best-match", title: t("configurationTemplate.entryPointBestMatch") },
        { key: "gift-selection", title: t("configurationTemplate.entryPointGiftSelection") },
        { key: "skin-care", title: t("configurationTemplate.entryPointSkinCare") },
        { key: "makeup-transfer", title: t("configurationTemplate.entryPointMakeupTransfer") },
      ],
    },
    {
      key: "language",
      title: t("configurationTemplate.languageTitle"),
      options: [
        { key: "english", title: t("configurationTemplate.languageEnglish") },
        { key: "portuguese", title: t("configurationTemplate.languagePortuguese") },
        { key: "lithuanian", title: t("configurationTemplate.languageLithuanian") },
        { key: "spanish", title: t("configurationTemplate.languageSpanish") },
        { key: "french", title: t("configurationTemplate.languageFrench") },
        { key: "italian", title: t("configurationTemplate.languageItalian") },
        { key: "german", title: t("configurationTemplate.languageGerman") },
      ],
    },
    {
      key: "version",
      title: t("configurationTemplate.versionTitle"),
      options: [
        { key: "standard", title: t("configurationTemplate.versionStandard") },
        {
          key: "recommendationSystem",
          title: t("configurationTemplate.versionRecommendationSystem"),
        },
      ],
    },
  ],
})
